import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { ArrowLeft, RefreshCw, FileText, Download } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BUCKET_LABEL = {
  current: 'Current (not yet due)',
  days_1_30: '1-30 days overdue',
  days_31_60: '31-60 days overdue',
  days_61_90: '61-90 days overdue',
  days_over_90: 'Over 90 days overdue',
};

const ARAPOpenItems = ({ mode }) => {
  // mode = 'AR' (customer open items) or 'AP' (vendor open items)
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const partnerId = params.partner_id;
  const bucket = searchParams.get('bucket'); // optional filter
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [partnerName, setPartnerName] = useState('');

  const isAR = mode === 'AR';
  const endpoint = isAR ? '/api/ar/open-items' : '/api/ap/open-items';
  const partnerKey = isAR ? 'customer_id' : 'vendor_id';
  const partnerNameKey = isAR ? 'customer_name' : 'vendor_name';
  const reportPath = isAR ? '/ar/aging-report' : '/ap/aging';
  const title = isAR ? 'Customer Open Items' : 'Vendor Open Items';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const queryParams = { [partnerKey]: partnerId };
      const r = await axios.get(`${backendUrl}${endpoint}`, { params: queryParams });
      let rows = r.data || [];

      // Normalize AR/AP shape: AR uses billing_number/original_amount/billing_date,
      // AP may use a different shape — coalesce into a common view model.
      const today = new Date();
      rows = rows.map((row) => {
        const docDate = row.posting_date || row.billing_date || row.invoice_date || null;
        const due = row.due_date ? new Date(row.due_date) : null;
        const daysOverdue = due ? Math.floor((today - due) / (1000 * 60 * 60 * 24)) : 0;
        return {
          ...row,
          item_id: row.item_id || row.document_number || row.billing_number || row.invoice_number,
          document_number: row.document_number || row.billing_number || row.invoice_number || row.item_id,
          document_type: row.document_type || (isAR ? 'INV' : 'KR'),
          posting_date: docDate,
          amount: row.original_amount ?? row.amount ?? 0,
          open_amount: row.open_amount ?? row.original_amount ?? row.amount ?? 0,
          days_overdue: daysOverdue,
          reference: row.reference || row.purchase_order_number || row.po_number || '',
        };
      });

      if (bucket) {
        rows = rows.filter((row) => {
          const d = row.days_overdue;
          switch (bucket) {
            case 'current': return d <= 0;
            case 'days_1_30': return d > 0 && d <= 30;
            case 'days_31_60': return d > 30 && d <= 60;
            case 'days_61_90': return d > 60 && d <= 90;
            case 'days_over_90': return d > 90;
            default: return true;
          }
        });
      }
      setItems(rows);
      if (rows[0]?.[partnerNameKey]) setPartnerName(rows[0][partnerNameKey]);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to load ${title.toLowerCase()}`);
    } finally { setLoading(false); }
  }, [backendUrl, endpoint, partnerKey, partnerId, partnerNameKey, bucket, title, isAR]);

  useEffect(() => { load(); }, [load]);

  const totals = items.reduce(
    (a, i) => {
      a.amount += Number(i.amount) || 0;
      a.open += Number(i.open_amount ?? i.amount) || 0;
      return a;
    },
    { amount: 0, open: 0 }
  );

  const downloadCsv = () => {
    if (!items.length) return;
    const headers = ['Document', 'Type', 'Posting Date', 'Due Date', 'Days Overdue', 'Amount', 'Open Amount', 'Currency', 'Reference', 'Status'];
    const lines = items.map((i) => [
      i.document_number, i.document_type, i.posting_date, i.due_date, i.days_overdue,
      i.amount, i.open_amount ?? i.amount, i.currency, i.reference, i.status,
    ].map((v) => `"${(v ?? '').toString().replace(/"/g, '""')}"`).join(','));
    const csv = [headers.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${mode.toLowerCase()}-open-items-${partnerId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid={`${mode.toLowerCase()}-open-items-page`}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(reportPath)}>
              <ArrowLeft className="w-4 h-4 mr-1" />Back to Aging Report
            </Button>
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <FileText className="w-7 h-7 text-orange-600" />
                {title}
              </h1>
              <div className="text-gray-600 mt-1 flex items-center gap-2 flex-wrap">
                <span>{partnerId}{partnerName && ` — ${partnerName}`}</span>
                {bucket && (
                  <Badge variant="secondary">
                    Filter: {BUCKET_LABEL[bucket] || bucket}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={downloadCsv} disabled={!items.length} data-testid={`${mode.toLowerCase()}-export-csv-btn`}>
              <Download className="w-4 h-4 mr-1" />Export CSV
            </Button>
            <Button variant="outline" onClick={load} data-testid={`${mode.toLowerCase()}-refresh-btn`}>
              <RefreshCw className="w-4 h-4 mr-1" />Refresh
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{items.length} Open Item(s)</CardTitle>
            <div className="text-sm text-gray-700 flex gap-6">
              <span>Σ Original <strong className="font-mono">{totals.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong></span>
              <span>Σ Open <strong className="font-mono text-amber-700">{totals.open.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong></span>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-10 text-gray-500">Loading…</div>
            ) : items.length === 0 ? (
              <div className="text-center py-10 text-gray-500">
                {bucket ? 'No items in this aging bucket.' : 'No open items for this partner.'}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Document #</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Posting Date</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead className="text-right">Days Overdue</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Open</TableHead>
                    <TableHead>Curr</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((i) => {
                    const d = i.days_overdue;
                    const overdueColor = d > 90 ? 'text-red-700 font-semibold'
                      : d > 60 ? 'text-red-600'
                        : d > 30 ? 'text-orange-600'
                          : d > 0 ? 'text-yellow-700' : 'text-emerald-600';
                    return (
                      <TableRow key={i.item_id || i.document_number} data-testid={`${mode.toLowerCase()}-open-row-${i.document_number}`}>
                        <TableCell className="font-mono">{i.document_number}</TableCell>
                        <TableCell><Badge variant="outline">{i.document_type || '-'}</Badge></TableCell>
                        <TableCell>{i.posting_date}</TableCell>
                        <TableCell>{i.due_date || '-'}</TableCell>
                        <TableCell className={`text-right font-mono ${overdueColor}`}>
                          {d > 0 ? d : (d === 0 ? '0' : '-')}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {Number(i.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right font-mono text-amber-700">
                          {Number(i.open_amount ?? i.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell>{i.currency || '-'}</TableCell>
                        <TableCell className="text-xs">{i.reference || '-'}</TableCell>
                        <TableCell>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            i.status === 'CLEARED' ? 'bg-emerald-100 text-emerald-700'
                              : i.status === 'PARTIALLY_CLEARED' ? 'bg-orange-100 text-orange-700'
                                : 'bg-amber-100 text-amber-800'
                          }`}>
                            {i.status || 'OPEN'}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default ARAPOpenItems;
